import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import AppLayout from '../components/AppLayout';

function ReportsStatsPage() {
    const [period, setPeriod] = useState('1m');

    const { data: reportData, isLoading, error } = useQuery({
        queryKey: ['cell-stats', period],
        queryFn: async () => {
            const response = await api.get(`/reports/cell-stats?period=${period}`);
            return response.data.data;
        }
    });

    if (isLoading) return <AppLayout><LoadingSpinner size="lg" /></AppLayout>;
    if (error) return <AppLayout><div className="alert alert-error">Erro ao carregar estatísticas: {error.message}</div></AppLayout>;

    const { hierarchy = {}, subtotal = {}, period_label } = reportData || {};

    return (
        <AppLayout>
            <div className="page-container" style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
                
                {/* Header & Filters */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
                    <div>
                        <h1 className="page-title" style={{ fontSize: '1.75rem', fontWeight: 800, color: '#172b4d' }}>Minhas Estatísticas</h1>
                        <p className="page-subtitle" style={{ color: '#5e6c84', marginTop: '4px' }}>
                            Análise de células sob sua supervisão (Período: {period_label})
                        </p>
                    </div>
                    <div>
                        <select 
                            className="form-control" 
                            value={period} 
                            onChange={(e) => setPeriod(e.target.value)}
                            style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #dfe1e6', backgroundColor: '#fff', fontWeight: 600, color: '#172b4d' }}
                        >
                            <option value="1m">Último Mês</option>
                            <option value="3m">Últimos 3 Meses</option>
                            <option value="6m">Últimos 6 Meses</option>
                            <option value="1a">Último Ano</option>
                        </select>
                    </div>
                </div>

                {/* Main Content - Hierarchy List (Option 2 - Density focus) */}
                <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #dfe1e6', overflow: 'hidden' }}>
                    {Object.keys(hierarchy).length === 0 ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#5e6c84' }}>
                            Nenhuma célula encontrada para a sua supervisão.
                        </div>
                    ) : (
                        <div>
                            {Object.entries(hierarchy).map(([seniorName, redes]) => (
                                <div key={seniorName} style={{ borderBottom: '2px solid #ebecf0' }}>
                                    <div style={{ padding: '12px 20px', background: '#f4f5f7', fontWeight: 800, color: '#172b4d', fontSize: '1.1rem' }}>
                                        Pastor Sênior: {seniorName}
                                    </div>
                                    
                                    {Object.entries(redes).map(([redeName, discipuladores]) => (
                                        <div key={redeName}>
                                            <div style={{ padding: '8px 20px', paddingLeft: '40px', background: '#fafbfc', fontWeight: 700, color: '#42526e', borderBottom: '1px solid #ebecf0' }}>
                                                Pastor de Rede: {redeName}
                                            </div>

                                            {Object.entries(discipuladores).map(([discipuladorName, cells]) => (
                                                <div key={discipuladorName}>
                                                    <div style={{ padding: '8px 20px', paddingLeft: '60px', color: '#5e6c84', fontWeight: 600, borderBottom: '1px solid #ebecf0', fontSize: '0.9rem' }}>
                                                        Discipulador: {discipuladorName}
                                                    </div>

                                                    <div style={{ paddingLeft: '80px' }}>
                                                        {cells.map((cell, idx) => (
                                                            <div key={cell.id} style={{ 
                                                                padding: '16px 20px', 
                                                                borderBottom: idx === cells.length - 1 ? 'none' : '1px solid #ebecf0',
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                                flexWrap: 'wrap',
                                                                gap: '16px'
                                                            }}>
                                                                {/* Cell Info */}
                                                                <div style={{ flex: '1 1 200px' }}>
                                                                    <div style={{ fontWeight: 700, color: '#172b4d' }}>Célula: {cell.name}</div>
                                                                    <div style={{ fontSize: '0.85rem', color: '#5e6c84', marginTop: '2px' }}>Líder: {cell.leader}</div>
                                                                </div>

                                                                {/* Metrics */}
                                                                <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', flex: '2 1 400px' }}>
                                                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                                        <span style={{ fontSize: '0.75rem', color: '#5e6c84', fontWeight: 600, textTransform: 'uppercase' }}>Membros</span>
                                                                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                                                                            <span style={{ fontSize: '1.25rem', fontWeight: 800, color: '#172b4d' }}>{cell.metrics.total_members}</span>
                                                                            <span style={{ fontSize: '0.85rem', color: cell.metrics.members_period > 0 ? '#006644' : '#5e6c84', fontWeight: 600 }}>
                                                                                {cell.metrics.members_period > 0 ? `+${cell.metrics.members_period} ` : ''}({cell.metrics.members_period_percent}%)
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                    
                                                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                                        <span style={{ fontSize: '0.75rem', color: '#5e6c84', fontWeight: 600, textTransform: 'uppercase' }}>Visitantes</span>
                                                                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                                                                            <span style={{ fontSize: '1.25rem', fontWeight: 800, color: '#172b4d' }}>{cell.metrics.total_visitors}</span>
                                                                            <span style={{ fontSize: '0.85rem', color: cell.metrics.visitors_period > 0 ? '#0052cc' : '#5e6c84', fontWeight: 600 }}>
                                                                                {cell.metrics.visitors_period > 0 ? `+${cell.metrics.visitors_period} ` : ''}({cell.metrics.visitors_period_percent}%)
                                                                            </span>
                                                                        </div>
                                                                    </div>

                                                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                                        <span style={{ fontSize: '0.75rem', color: '#5e6c84', fontWeight: 600, textTransform: 'uppercase' }}>F.A.</span>
                                                                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                                                                            <span style={{ fontSize: '1.25rem', fontWeight: 800, color: '#172b4d' }}>{cell.metrics.total_fa}</span>
                                                                            <span style={{ fontSize: '0.85rem', color: cell.metrics.fa_period > 0 ? '#403294' : '#5e6c84', fontWeight: 600 }}>
                                                                                {cell.metrics.fa_period > 0 ? `+${cell.metrics.fa_period} ` : ''}({cell.metrics.fa_period_percent}%)
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Courses */}
                                                                <div style={{ flex: '1 1 150px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                                    <span style={{ fontSize: '0.75rem', color: '#5e6c84', fontWeight: 600, textTransform: 'uppercase' }}>Cursos (% Membros)</span>
                                                                    <div style={{ display: 'flex', gap: '8px', fontSize: '0.8rem', fontWeight: 700 }}>
                                                                        <span style={{ padding: '2px 6px', background: '#e3fcef', color: '#006644', borderRadius: '4px' }}>CME {cell.metrics.cme_percent}%</span>
                                                                        <span style={{ padding: '2px 6px', background: '#deebff', color: '#0052cc', borderRadius: '4px' }}>CTL {cell.metrics.ctl_percent}%</span>
                                                                        <span style={{ padding: '2px 6px', background: '#eae6ff', color: '#403294', borderRadius: '4px' }}>STP {cell.metrics.stp_percent}%</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Subtotal Footer */}
                    {Object.keys(hierarchy).length > 0 && (
                        <div style={{ 
                            background: '#172b4d', 
                            color: '#fff', 
                            padding: '24px', 
                            display: 'flex', 
                            flexWrap: 'wrap', 
                            gap: '24px', 
                            justifyContent: 'space-between',
                            borderTop: '2px solid #091e42'
                        }}>
                            <div style={{ fontSize: '1.25rem', fontWeight: 800 }}>SUBTOTAL GERAL</div>
                            <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ fontSize: '0.75rem', color: '#b3bac5', fontWeight: 600, textTransform: 'uppercase' }}>Membros</span>
                                    <span style={{ fontSize: '1.5rem', fontWeight: 800 }}>{subtotal.total_members} <span style={{ fontSize: '1rem', color: '#57d9a3' }}>(+{subtotal.members_period_percent}%)</span></span>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ fontSize: '0.75rem', color: '#b3bac5', fontWeight: 600, textTransform: 'uppercase' }}>Visitantes</span>
                                    <span style={{ fontSize: '1.5rem', fontWeight: 800 }}>{subtotal.total_visitors} <span style={{ fontSize: '1rem', color: '#4c9aff' }}>(+{subtotal.visitors_period_percent}%)</span></span>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ fontSize: '0.75rem', color: '#b3bac5', fontWeight: 600, textTransform: 'uppercase' }}>F.A.</span>
                                    <span style={{ fontSize: '1.5rem', fontWeight: 800 }}>{subtotal.total_fa} <span style={{ fontSize: '1rem', color: '#998dd9' }}>(+{subtotal.fa_period_percent}%)</span></span>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ fontSize: '0.75rem', color: '#b3bac5', fontWeight: 600, textTransform: 'uppercase' }}>Cursos (Média)</span>
                                    <div style={{ display: 'flex', gap: '8px', fontSize: '0.85rem', fontWeight: 700, marginTop: '4px' }}>
                                        <span style={{ color: '#57d9a3' }}>CME {subtotal.cme_percent}%</span>
                                        <span style={{ color: '#4c9aff' }}>CTL {subtotal.ctl_percent}%</span>
                                        <span style={{ color: '#998dd9' }}>STP {subtotal.stp_percent}%</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

            </div>
        </AppLayout>
    );
}

export default ReportsStatsPage;
